import apiClient from '@/lib/axios'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface BranchSettings {
  timezone?: string
  currency?: string
  llmProvider?: string
  llmModel?: string
  llmSystemPrompt?: string
  sttProvider?: string
  sttModel?: string
  sttLanguage?: string
  ttsProvider?: string
  ttsVoiceId?: string
  realtimeVoice?: string
  realtimeVadThreshold?: number
  realtimeSilenceMs?: number
  knowledgeBase?: string
}

export interface Branch {
  id: string
  restaurantId: string
  name: string
  address: string | null
  settings: BranchSettings
  createdAt: string
  updatedAt: string
}

export interface CreateBranchInput {
  name: string
  address?: string
  settings?: BranchSettings
}

export interface UpdateBranchInput {
  name?: string
  address?: string
  settings?: BranchSettings
}

export function useBranches(enabled: boolean = true) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  // Query for fetching branches list
  const branchesQuery = useQuery<Branch[]>({
    queryKey: ['branches'],
    queryFn: async () => {
      const token = await getToken()
      const response = await apiClient.get<Branch[]>('/branches', {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      })
      return response.data
    },
    enabled, // Only fetch if enabled
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  })

  // Mutation for creating a branch
  const createMutation = useMutation({
    mutationFn: async (data: CreateBranchInput) => {
      const token = await getToken()
      const response = await apiClient.post<Branch>('/branches', data, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      })
      return response.data
    },
    onMutate: async (newBranch) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['branches'] })

      // Snapshot previous value
      const previousBranches = queryClient.getQueryData<Branch[]>(['branches'])

      // Optimistically update
      queryClient.setQueryData<Branch[]>(['branches'], (old) => {
        const optimisticBranch: Branch = {
          id: `temp-${Date.now()}`,
          restaurantId: '',
          name: newBranch.name,
          address: newBranch.address || null,
          settings: newBranch.settings || {},
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        return old ? [...old, optimisticBranch] : [optimisticBranch]
      })

      return { previousBranches }
    },
    onError: (_err, _newBranch, context) => {
      // Rollback on error
      if (context?.previousBranches) {
        queryClient.setQueryData(['branches'], context.previousBranches)
      }
    },
    onSuccess: () => {
      // Refetch to get the actual data from server
      queryClient.invalidateQueries({ queryKey: ['branches'] })
    },
  })

  // Mutation for updating a branch
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateBranchInput }) => {
      const token = await getToken()
      const response = await apiClient.patch<Branch>(`/branches/${id}`, data, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      })
      return response.data
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: ['branches'] })
      const previousBranches = queryClient.getQueryData<Branch[]>(['branches'])

      queryClient.setQueryData<Branch[]>(['branches'], (old) => {
        if (!old) return old
        return old.map((branch) =>
          branch.id === id
            ? { ...branch, ...data, updatedAt: new Date().toISOString() }
            : branch
        )
      })

      return { previousBranches }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousBranches) {
        queryClient.setQueryData(['branches'], context.previousBranches)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] })
    },
  })

  // Mutation for deleting a branch
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken()
      await apiClient.delete(`/branches/${id}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      })
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['branches'] })
      const previousBranches = queryClient.getQueryData<Branch[]>(['branches'])

      queryClient.setQueryData<Branch[]>(['branches'], (old) => {
        if (!old) return old
        return old.filter((branch) => branch.id !== id)
      })

      return { previousBranches }
    },
    onError: (_err, _id, context) => {
      if (context?.previousBranches) {
        queryClient.setQueryData(['branches'], context.previousBranches)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] })
    },
  })

  return {
    branches: {
      data: branchesQuery.data,
      isLoading: branchesQuery.isLoading,
      error: branchesQuery.error,
      refetch: branchesQuery.refetch,
    },
    createBranch: {
      mutate: createMutation.mutate,
      mutateAsync: createMutation.mutateAsync,
      isLoading: createMutation.isPending,
      error: createMutation.error,
    },
    updateBranch: {
      mutate: (id: string, data: UpdateBranchInput) => updateMutation.mutate({ id, data }),
      mutateAsync: (id: string, data: UpdateBranchInput) => updateMutation.mutateAsync({ id, data }),
      isLoading: updateMutation.isPending,
      error: updateMutation.error,
    },
    deleteBranch: {
      mutate: deleteMutation.mutate,
      mutateAsync: deleteMutation.mutateAsync,
      isLoading: deleteMutation.isPending,
      error: deleteMutation.error,
    },
  }
}

